// Data-driven team profile for /team/:slug.
//
// WHY: Team.tsx renders a "View Full Profile" button for every active team_members row, but routing
// only ever had two hand-built pages (mark-insley, sam-hallett) and no fallback — so half the team
// (nanga-roto, bongiwe-sithebe) linked straight to NotFound on the public marketing site.
//
// This renders from the team_members row itself, so adding a member, or filling in a bio/photo,
// updates the site with no code change. The two bespoke pages keep their own richer hand-built
// routes (static segments outrank :slug in React Router, so they always win).
//
// Fields degrade individually: several members currently have a row and a position but an empty bio
// and no photo/LinkedIn. Each block is omitted when empty rather than rendering an empty heading, so
// a sparse record still produces a clean page instead of a broken-looking one.

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Mail, LinkedinIcon, ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface TeamMember {
  name: string;
  position: string;
  bio: string | null;
  email: string | null;
  linkedin_url: string | null;
  photo_url: string | null;
  slug: string;
}

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("");

const TeamMemberProfile = () => {
  const { slug } = useParams<{ slug: string }>();
  const [member, setMember] = useState<TeamMember | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("team_members")
        .select("name, position, bio, email, linkedin_url, photo_url, slug")
        .eq("slug", slug)
        .eq("status", "active")
        .maybeSingle();
      if (!cancelled) {
        setMember((data as TeamMember) ?? null);
        setLoading(false);
      }
    };
    if (slug) void load();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 space-y-6">
          <Skeleton className="h-32 w-32 rounded-full" />
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      </div>
    );
  }

  // Unknown or inactive slug — say so plainly rather than rendering an empty shell.
  if (!member) {
    return (
      <div className="py-24">
        <SEO title="Profile not found | Cape Berkshire Asset Management" description="This team profile could not be found." />
        <div className="mx-auto max-w-2xl px-4 text-center">
          <h1 className="text-3xl font-bold mb-4">Profile not found</h1>
          <p className="text-muted-foreground mb-8">
            We couldn't find that team member. They may no longer be listed.
          </p>
          <Button asChild>
            <Link to="/team"><ArrowLeft className="h-4 w-4 mr-2" />Back to the team</Link>
          </Button>
        </div>
      </div>
    );
  }

  const hasBio = Boolean(member.bio && member.bio.trim());

  return (
    <div>
      <SEO
        title={`${member.name} — ${member.position} | Cape Berkshire Asset Management`}
        description={
          hasBio
            ? `${member.bio!.trim().slice(0, 155)}`
            : `${member.name}, ${member.position} at Cape Berkshire Asset Management.`
        }
      />

      <section className="bg-gradient-to-br from-primary to-accent text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mx-auto mb-6 h-32 w-32 rounded-full overflow-hidden bg-primary-foreground/10 flex items-center justify-center border-4 border-primary-foreground/20">
              {member.photo_url ? (
                <img src={member.photo_url} alt={member.name} className="h-full w-full object-cover" />
              ) : (
                <span className="text-4xl font-semibold">{initials(member.name)}</span>
              )}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-3">{member.name}</h1>
            <p className="text-xl text-primary-foreground/90">{member.position}</p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              {hasBio ? (
                <>
                  <h2 className="text-3xl font-bold mb-6">Biography</h2>
                  {member.bio!.trim().split(/\n{2,}/).map((para, i) => (
                    <p key={i} className="text-muted-foreground mb-4 leading-relaxed">{para}</p>
                  ))}
                </>
              ) : (
                <p className="text-muted-foreground">
                  {member.name} is {member.position} at Cape Berkshire Asset Management.
                </p>
              )}
              <Button variant="ghost" asChild className="mt-8 -ml-3">
                <Link to="/team"><ArrowLeft className="h-4 w-4 mr-2" />Back to the team</Link>
              </Button>
            </div>

            {(member.email || member.linkedin_url) && (
              <div>
                <Card>
                  <CardHeader><CardTitle>Contact</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {member.email && (
                      <Button variant="outline" className="w-full justify-start" asChild>
                        <a href={`mailto:${member.email}`}>
                          <Mail className="h-4 w-4 mr-2" />{member.email}
                        </a>
                      </Button>
                    )}
                    {member.linkedin_url && (
                      <Button variant="outline" className="w-full justify-start" asChild>
                        <a href={member.linkedin_url} target="_blank" rel="noopener noreferrer">
                          <LinkedinIcon className="h-4 w-4 mr-2" />LinkedIn
                        </a>
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeamMemberProfile;
